import { BaseApiService } from '@doccareplus/api-client';

// HTTP adapter for the patient-portal appointments endpoints (api/portal/appointments/*). Auth is by
// PatientPortalLink ownership — the requesting user + active link are resolved server-side from the JWT and
// the X-Portal-Link-Id header (applied globally, see profiles' `applyActiveProfileHeader`), never sent
// per-request here. All JSON is camelCase; these types mirror the C# Contracts.Portal.Appointments records 1:1.

export type PortalAppointmentStatus =
  | 'Requested'
  | 'Confirmed'
  | 'CheckedIn'
  | 'InProgress'
  | 'Completed'
  | 'Cancelled'
  | 'NoShow'
  | 'Rejected';

/** Mirrors Contracts.Portal.Appointments.Common.PortalAppointmentDto. */
export interface PortalAppointmentDto {
  id: string;
  status: PortalAppointmentStatus;
  startTime: string;
  durationMinutes: number;
  providerId: string | null;
  serviceId: string | null;
  reason: string | null;
  patientNote: string | null;
  cancellationReason: string | null;
  checkedInAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  rescheduleRequest: AppointmentRescheduleRequestDto | null;
}

/** Mirrors Contracts.Portal.Appointments.Common.AppointmentRescheduleRequestDto. */
export interface AppointmentRescheduleRequestDto {
  id: string;
  appointmentId: string;
  proposedStartTime: string;
  reason: string | null;
  status: 'Pending' | 'Approved' | 'Rejected';
  requestedAt: string;
  reviewedAt: string | null;
}

export interface RequestAppointmentBody {
  startTime: string;
  providerId: string;
  serviceId?: string | null;
  reason?: string | null;
}

export interface PortalBookingProviderDto {
  id: string;
  name: string;
  specialty: string | null;
}

export interface PortalBookingServiceDto {
  id: string;
  name: string;
}

export interface PortalBookingDayDto {
  date: string;
  slots: Array<{ startTime: string }>;
}

export interface PortalBookingOptionsResponse {
  timezone: string;
  providers: PortalBookingProviderDto[];
  services: PortalBookingServiceDto[];
  days: PortalBookingDayDto[];
}

export interface PortalBookingOptionsParams {
  providerId?: string | null;
  serviceId?: string | null;
  fromDate?: string;
  days?: number;
}

export interface CancelAppointmentBody {
  reason?: string | null;
}

export interface RequestRescheduleBody {
  proposedStartTime: string;
  reason?: string | null;
}

export interface MyAppointmentsResponse {
  pending: PortalAppointmentDto[];
  upcoming: PortalAppointmentDto[];
  past: PortalAppointmentDto[];
}

export interface PortalClinicDto {
  clinicId: string;
  name: string;
  city: string | null;
  stateCode: string | null;
  countryCode: string | null;
  timezone: string;
  phone: string | null;
  type: string | null;
}

export interface ListPortalClinicsResponse {
  clinics: PortalClinicDto[];
}

export interface PortalClinicAppointmentRequestDto {
  clinicId: string;
  clinicName: string;
  timezone: string;
  appointment: PortalAppointmentDto;
}

export interface ListUnlinkedAppointmentRequestsResponse {
  requests: PortalClinicAppointmentRequestDto[];
}

export type RequestClinicAppointmentBody = RequestAppointmentBody;

class AppointmentsApiService extends BaseApiService {
  constructor() {
    super('/portal');
  }

  request(body: RequestAppointmentBody): Promise<PortalAppointmentDto> {
    return this.post<PortalAppointmentDto, RequestAppointmentBody>('/appointments', body);
  }

  bookingOptions(params: PortalBookingOptionsParams = {}): Promise<PortalBookingOptionsResponse> {
    return this.get<PortalBookingOptionsResponse>('/appointments/booking-options', {
      params: {
        ...(params.providerId ? { providerId: params.providerId } : {}),
        ...(params.serviceId ? { serviceId: params.serviceId } : {}),
        ...(params.fromDate ? { fromDate: params.fromDate } : {}),
        days: params.days ?? 14,
      },
    });
  }

  list(): Promise<MyAppointmentsResponse> {
    return this.get<MyAppointmentsResponse>('/appointments');
  }

  listClinics(search = ''): Promise<ListPortalClinicsResponse> {
    return this.get<ListPortalClinicsResponse>('/clinics', {
      params: search.trim() ? { search: search.trim() } : {},
    });
  }

  listUnlinkedRequests(): Promise<ListUnlinkedAppointmentRequestsResponse> {
    return this.get<ListUnlinkedAppointmentRequestsResponse>('/clinic-appointment-requests');
  }

  clinicBookingOptions(
    clinicId: string,
    params: PortalBookingOptionsParams = {}
  ): Promise<PortalBookingOptionsResponse> {
    return this.get<PortalBookingOptionsResponse>(`/clinics/${clinicId}/booking-options`, {
      params: {
        ...(params.providerId ? { providerId: params.providerId } : {}),
        ...(params.serviceId ? { serviceId: params.serviceId } : {}),
        ...(params.fromDate ? { fromDate: params.fromDate } : {}),
        days: params.days ?? 14,
      },
    });
  }

  requestClinicAppointment(
    clinicId: string,
    body: RequestClinicAppointmentBody
  ): Promise<PortalAppointmentDto> {
    return this.post<PortalAppointmentDto, RequestClinicAppointmentBody>(
      `/clinics/${clinicId}/appointment-requests`,
      body
    );
  }

  cancel(appointmentId: string, body: CancelAppointmentBody): Promise<PortalAppointmentDto> {
    return this.post<PortalAppointmentDto, CancelAppointmentBody>(`/appointments/${appointmentId}/cancel`, body);
  }

  requestReschedule(appointmentId: string, body: RequestRescheduleBody): Promise<AppointmentRescheduleRequestDto> {
    return this.post<AppointmentRescheduleRequestDto, RequestRescheduleBody>(
      `/appointments/${appointmentId}/reschedule-request`,
      body
    );
  }

  rescheduleRequested(appointmentId: string, body: RequestRescheduleBody): Promise<PortalAppointmentDto> {
    return this.post<PortalAppointmentDto, RequestRescheduleBody>(
      `/appointments/${appointmentId}/reschedule`,
      body
    );
  }
}

export const appointmentsApi = new AppointmentsApiService();
